import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Sparkles, ArrowLeft, Save, Download } from 'lucide-react';
import { toast } from 'sonner';
import EditorSidebar from '../components/editor/EditorSidebar';
import EditorCanvas from '../components/editor/EditorCanvas';
import EditorToolbar from '../components/editor/EditorToolbar';
import ZoomControls from '../components/editor/ZoomControls';
import { generatePDF } from '../utils/pdfGenerator';
import { generatePolaroidPDF } from '../utils/polaroidGenerator';

export default function CanvaEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { badge, orientation } = location.state || {};

  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [selectedBadgeIndex, setSelectedBadgeIndex] = useState(0);
  const [badges, setBadges] = useState([]);
  const [editMode, setEditMode] = useState('individual'); // 'individual' or 'all'
  const [isPanning, setIsPanning] = useState(false); // Kept for future panning logic
  const [showPolaroidFrame, setShowPolaroidFrame] = useState(true);
  const [uploadedImages, setUploadedImages] = useState([]);

  // Calculate badge grid layout
  useEffect(() => {
    if (!badge) return;

    const a4Width = 210;
    const a4Height = 297;
    const margin = 10;
    const gutter = 5;

    let designWidth, designHeight;
    if (badge.type === 'round' || badge.type === 'square') {
      designWidth = designHeight = badge.designArea;
    } else {
      if (orientation === 'landscape') {
        designWidth = badge.designWidth;
        designHeight = badge.designHeight;
      } else {
        designWidth = badge.designHeight;
        designHeight = badge.designWidth;
      }
    }

    const availableWidth = a4Width - (2 * margin);
    const availableHeight = a4Height - (2 * margin);

    const cols = Math.floor((availableWidth + gutter) / (designWidth + gutter));
    const rows = Math.floor((availableHeight + gutter) / (designHeight + gutter));

    const spacingX = (availableWidth - (cols * designWidth)) / (cols + 1);
    const spacingY = (availableHeight - (rows * designHeight)) / (rows + 1);

    // Initialize badges
    const initialBadges = [];
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = margin + spacingX + col * (designWidth + spacingX);
        const y = margin + spacingY + row * (designHeight + spacingY);

        initialBadges.push({
          id: `badge-${row}-${col}`,
          x,
          y,
          width: designWidth,
          height: designHeight,
          layers: [
            { type: 'background', color: '#ffffff', locked: true }
          ]
        });
      }
    }

    setBadges(initialBadges);
  }, [badge, orientation]);

  const handleUndo = () => {
    toast.info('Undo feature coming soon');
  };

  const handleRedo = () => {
    toast.info('Redo feature coming soon');
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 5));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.25));
  };

  const handleFitToScreen = () => {
    setZoom(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const handleExport = async () => {
    try {
      toast.loading('Generating PDF...');
      // TODO: Implement full export with layers
      if (badge.type === 'polaroid') {
        await generatePolaroidPDF({
          badge,
          badges,
          orientation,
          showGuides: true,
          showFrame: showPolaroidFrame
        });
      } else {
        await generatePDF({
          badge,
          badges,
          orientation,
          zoom,
          showGuides: true
        });
      }
      toast.dismiss();
      toast.success('PDF exported successfully!');
    } catch (error) {
      toast.dismiss();
      toast.error('Export failed');
      console.error(error);
    }
  };

  const handleAddImage = (imageData) => {
    const newLayer = {
      type: 'image',
      id: `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      url: imageData.url,
      x: 0,
      y: 0,
      width: 100,
      height: 100,
      rotation: 0,
      locked: false,
      scale: 1,
      offsetX: 0,
      offsetY: 0,
      fitMode: 'cover'
    };

    // Track uploaded image
    setUploadedImages(prev => [...prev, imageData]);

    if (editMode === 'all') {
      const updatedBadges = badges.map(b => {
        const nonImageLayers = b.layers.filter(l => l.type !== 'image');
        return {
          ...b,
          layers: [...nonImageLayers, { ...newLayer, id: `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` }]
        };
      });
      setBadges(updatedBadges);
      toast.success(`Image added to all ${badges.length} badges!`);
    } else {
      const updatedBadges = badges.map((b, i) => {
        if (i === selectedBadgeIndex) {
          const nonImageLayers = b.layers.filter(l => l.type !== 'image');
          return { ...b, layers: [...nonImageLayers, { ...newLayer }] };
        }
        return b;
      });
      setBadges(updatedBadges);
      toast.success(`Image added to badge ${selectedBadgeIndex + 1}!`);

      const nextIndex = selectedBadgeIndex + 1;
      if (nextIndex < badges.length) {
        setSelectedBadgeIndex(nextIndex);
      }
    }
  };

  const handleBulkUpload = (images) => {
    // Track all uploaded images
    setUploadedImages(images);

    // Assign each image to a badge in order
    const updatedBadges = badges.map((b, index) => {
      if (index < images.length) {
        const newLayer = {
          type: 'image',
          id: `img-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
          url: images[index].url,
          x: 0,
          y: 0,
          width: 100,
          height: 100,
          rotation: 0,
          locked: false,
          scale: 1,
          offsetX: 0,
          offsetY: 0,
          fitMode: 'cover'
        };
        const nonImageLayers = b.layers.filter(l => l.type !== 'image');
        return { ...b, layers: [...nonImageLayers, newLayer] };
      }
      return b;
    });

    setBadges(updatedBadges);
  };

  if (!badge) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold">No badge selected</h2>
          <Button onClick={() => navigate('/maker')} className="rounded-xl">
            Choose Badge Size
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-muted/30">
      {/* Top Bar */}
      <header className="border-b border-border bg-card px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/maker')}
            className="rounded-xl"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-primary-foreground" />
            </div>
            <div>
              <h1 className="font-bold text-sm">BadgeMaker Studio</h1>
              <p className="text-xs text-muted-foreground">{badge.label}</p>
            </div>
          </div>
        </div>

        <EditorToolbar
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={false}
          canRedo={false}
          editMode={editMode}
          onEditModeChange={setEditMode}
        />

        <div className="flex items-center gap-4">
          {badge?.type === 'polaroid' && (
            <div className="flex items-center gap-2 mr-2">
              <span className="text-sm font-medium text-muted-foreground hidden sm:block">Polaroid Style</span>
              <div
                onClick={() => setShowPolaroidFrame(!showPolaroidFrame)}
                className={`w-10 h-6 rounded-full cursor-pointer transition-colors duration-200 relative ${showPolaroidFrame ? 'bg-pink-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform duration-200 shadow-sm ${showPolaroidFrame ? 'left-5' : 'left-1'}`} />
              </div>
            </div>
          )}

          <Button variant="outline" size="sm" className="rounded-xl">
            <Save className="w-4 h-4 mr-2" />
            Save
          </Button>
          <Button
            onClick={handleExport}
            size="sm"
            className="rounded-xl bg-gradient-to-r from-primary to-secondary"
          >
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </header>

      {/* Main Editor */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar */}
        <EditorSidebar
          onAddImage={handleAddImage}
          onBulkUpload={handleBulkUpload}
          badgeCount={badges.length}
          uploadedImages={uploadedImages}
        />

        {/* Canvas Area */}
        <div className="flex-1 relative bg-muted/50 overflow-auto">
          <div
            className="min-w-full min-h-full flex items-center justify-center p-8"
            style={{
              // Ensure content is at least as large as container for proper centering
              minWidth: 'max-content',
              minHeight: 'max-content'
            }}
          >
            {(() => {
              // Construct a localized badge object for the canvas that respects orientation
              let currentBadge = { ...badge };
              if (orientation === 'portrait' && (badge.type === 'polaroid' || badge.type === 'rectangle')) {
                // Swap for portrait
                currentBadge.badgeWidth = badge.badgeHeight;
                currentBadge.badgeHeight = badge.badgeWidth;
                currentBadge.designWidth = badge.designHeight;
                currentBadge.designHeight = badge.designWidth;
              }

              return (
                <EditorCanvas
                  ref={canvasRef}
                  badge={currentBadge}
                  badges={badges}
                  orientation={orientation}
                  zoom={zoom}
                  panOffset={panOffset}
                  selectedBadgeIndex={selectedBadgeIndex}
                  onSelectBadge={setSelectedBadgeIndex}
                  onUpdateBadges={setBadges}
                />
              );
            })()}
          </div>

          {/* Zoom Controls */}
          <ZoomControls
            zoom={zoom}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onFitToScreen={handleFitToScreen}
            onZoomChange={setZoom}
          />
        </div>
      </div>
    </div>
  );
}
